import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { useClientsListQuery, useClientDetailQuery, usePortalInvalidate, usePrefetchDetailOnHover } from "@/hooks/usePortalQueries";
import { SyncPill } from "@/components/SyncPill";
import { RecordFormModal } from "@/components/RecordFormModal";
import { NewClientForm } from "@/components/NewClientForm";
import { ArchiveControls } from "@/components/ArchiveControls";
import { ActivityBadge } from "@/components/ActivityBadge";
import { StatusChip, TONE_CHIP_CLS, TONE_DOT_CLS } from "@/components/StatusChip";
import { HealthMarker, HEALTH_KEYS } from "@/components/HealthMarker";
import { StageGlyph } from "@/components/StageGlyph";
import { EmptyState } from "@/components/EmptyState";
import { DetailSkeleton } from "@/components/Skeletons";
import { Section, SectionPanel } from "@/components/Section";
import { RecordPageLayout, RecordTag } from "@/components/RecordPageLayout";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { ClientPortalVisibilityToggle } from "@/components/ClientPortalVisibilityToggle";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";
import { EstimatorEnabledToggle } from "@/components/EstimatorEnabledToggle";
import { openClientPortalPreview } from "@/lib/clientPortalPreview";
import { formatMoney } from "@/lib/money";
import { Sparkles, ExternalLink, Mail, Receipt, Plus, Wallet, Pencil, Search, MoreHorizontal, ArrowRight, ShieldCheck, Eye, RefreshCw, Copy } from "lucide-react";

const CLOSED_STATUSES = new Set(["Delivered", "Archived", "Lost"]);
const lastContactLabel = (days) => days === null || days === undefined ? "—" : days === 0 ? "Today" : days === 1 ? "1 day ago" : `${days} days ago`;
const PAGE_SIZE = 30;
const SEGMENTS = [
  { key: "active", label: "Active" },
  { key: "recent", label: "Recent" },
  { key: "all", label: "All" },
  { key: "archived", label: "Archived" },
];
// Notion's free-text Health field never had a pre-existing colour/tone — it rendered as a
// flat grey Chip regardless of value, so mapping it onto HealthMarker's tone-coded dot here
// is a NEW visual (not a changed meaning). An unrecognized value still shows as a plain tag,
// never silently dropped.
const toHealthKey = (h) => (h || "").toString().trim().toLowerCase().replace(/\s+/g, "_");

export function ClientsList() {
  const { hasScope } = useAuth();
  const navigate = useNavigate();
  const invalidatePortal = usePortalInvalidate();
  const prefetchClient = usePrefetchDetailOnHover("client");
  // Wave 1 (2026-10, frontend data cache) point 4 — segment/page/search live in the URL so
  // Back restores the exact same filtered, paginated view a PM was looking at.
  const [searchParams, setSearchParams] = useSearchParams();
  const segment = SEGMENTS.some((s) => s.key === searchParams.get("segment")) ? searchParams.get("segment") : "active";
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
  const search = searchParams.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const setSegment = (v) => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set("segment", v); n.delete("page"); return n; });
  const setPage = (v) => setSearchParams((prev) => { const n = new URLSearchParams(prev); v > 1 ? n.set("page", String(v)) : n.delete("page"); return n; });

  // Debounce: search box updates `searchInput` on every keystroke, the URL `q` (which drives
  // the query key) only settles 300ms after typing stops — and resets to page 1.
  const selfUpdatedSearchRef = useRef(false);
  useEffect(() => {
    if (searchInput.trim() === search) return;
    const t = setTimeout(() => {
      selfUpdatedSearchRef.current = true;
      setSearchParams((prev) => {
        const n = new URLSearchParams(prev);
        const trimmed = searchInput.trim();
        trimmed ? n.set("q", trimmed) : n.delete("q");
        n.delete("page");
        return n;
      }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    if (selfUpdatedSearchRef.current) { selfUpdatedSearchRef.current = false; return; }
    setSearchInput(search);
  }, [search]);

  const [showNew, setShowNew] = useState(false);
  const [newProjectClient, setNewProjectClient] = useState(null);

  const { data, isLoading: loading, isFetching: fetching, isError: error, refetch } = useClientsListQuery(segment, page, search, PAGE_SIZE);
  const items = data?.items || [];
  const segmentCounts = data?.segment_counts || {};
  const total = data?.total || 0;
  const load = () => { invalidatePortal.clientLists(); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-8" data-testid="clients-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-editorial text-4xl tracking-tight text-ink">Clients</h1>
        {hasScope("clients.create") && (
          <button type="button" data-testid="new-client-btn" onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink hover:bg-brand-dim">
            <Plus className="h-3.5 w-3.5" /> New Client
          </button>
        )}
      </div>
      <NewClientForm open={showNew} onClose={() => setShowNew(false)}
        onCreated={(record, wantsNewProject) => { load(); if (wantsNewProject) setNewProjectClient(record); }} />
      <RecordFormModal type="project" open={!!newProjectClient} onClose={() => setNewProjectClient(null)}
        defaultClientId={newProjectClient?.id} defaultClientName={newProjectClient?.name}
        onCreated={(record, opts) => { setNewProjectClient(null); invalidatePortal.projectLists(); if (opts?.openDetail && record?.id) navigate(`/projects/${record.id}`); }} />

      {/* Segments — appear immediately, never wait on the network */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-1 rounded-md border border-hairline bg-paper p-1" data-testid="clients-segments">
          {SEGMENTS.map((s) => (
            <button type="button" key={s.key} onClick={() => setSegment(s.key)} data-testid={`clients-segment-${s.key}`}
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${segment === s.key ? "bg-brand text-ink" : "text-ink-soft hover:text-ink"}`}>
              {s.label}{segmentCounts[s.key] !== undefined && <span className="ml-1.5 text-[10px] opacity-70 font-mono">{segmentCounts[s.key]}</span>}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by client name…"
            data-testid="clients-search-input"
            className="w-64 rounded-md border border-hairline bg-paper pl-8 pr-3 py-1.5 text-xs text-ink placeholder:text-faint focus:outline-none focus:border-brand"
          />
          {fetching && <RefreshCw className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-faint animate-spin" data-testid="clients-fetching-indicator" />}
        </div>
      </div>

      {error && items.length === 0 ? (
        <div className="rounded-lg border border-tone-red-text/20 bg-tone-red-bg p-8 text-center" data-testid="clients-error-state">
          <p className="text-sm text-tone-red-text">Couldn't load clients.</p>
          <button type="button" onClick={refetch} className="mt-3 rounded-md border border-tone-red-text/30 px-3 py-1.5 text-xs text-tone-red-text hover:bg-tone-red-bg/70" data-testid="clients-retry-btn">Retry</button>
        </div>
      ) : loading ? (
        <ClientsRowsSkeleton />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-hairline bg-paper">
          {search ? (
            <EmptyState title={`No clients match "${search}"`} hint="Try a different name." testId="clients-empty-search" />
          ) : (
            <EmptyState title={`No ${segment} clients`} hint={segment === "active" ? "Add a client to get started." : "Nothing here right now."} testId="clients-empty"
              actionLabel={segment === "active" && hasScope("clients.create") ? "+ New Client" : undefined}
              onAction={segment === "active" && hasScope("clients.create") ? () => setShowNew(true) : undefined} />
          )}
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-3 flex items-center justify-between rounded-md border border-tone-gold-text/40 bg-tone-gold-bg px-3 py-2 text-xs text-tone-gold-text" data-testid="clients-refresh-error">
              <span>Showing a saved copy — unable to refresh just now.</span>
              <button type="button" onClick={refetch} data-testid="clients-refresh-retry" className="font-semibold underline">Retry</button>
            </div>
          )}
          <div className={`rounded-lg border border-hairline bg-paper overflow-hidden transition-opacity ${fetching ? "opacity-60" : ""}`} data-testid="clients-rows">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-[10px] font-semibold uppercase tracking-wider text-faint">
                  <th className="text-left px-4 py-3">Client</th>
                  <th className="text-left px-4 py-3">Open Projects</th>
                  <th className="text-left px-4 py-3">Last Contact</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => <ClientRow key={c.id} client={c} onChanged={load} prefetchProps={prefetchClient} />)}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !error && total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-xs text-faint" data-testid="clients-pagination">
          <span>Page {page} of {totalPages} · {total} clients</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} data-testid="clients-page-prev"
              className="rounded-md border border-hairline px-3 py-1.5 disabled:opacity-40 hover:border-brand">Previous</button>
            <button type="button" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid="clients-page-next"
              className="rounded-md border border-hairline px-3 py-1.5 disabled:opacity-40 hover:border-brand">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

const ClientsRowsSkeleton = () => (
  <div className="rounded-lg border border-hairline bg-paper divide-y divide-hairline" data-testid="clients-skeleton">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-4 animate-pulse">
        <div className="h-3.5 w-40 rounded bg-surface2" />
        <div className="h-3.5 w-20 rounded bg-surface2 ml-auto" />
        <div className="h-3.5 w-24 rounded bg-surface2" />
        <div className="h-6 w-16 rounded bg-surface2" />
      </div>
    ))}
  </div>
);

const SYNC_TROUBLE_STATES = new Set(["stale", "conflict"]);

function ClientRow({ client: c, onChanged, prefetchProps }) {
  const { hasScope } = useAuth();
  const [previewing, setPreviewing] = useState(false);
  const remoteId = c.sync?.remote_id;
  const syncTrouble = SYNC_TROUBLE_STATES.has(c.sync?.state);

  const previewAsClient = async () => {
    if (!remoteId) return;
    setPreviewing(true);
    await openClientPortalPreview(remoteId, c.name);
    setPreviewing(false);
  };

  return (
    <tr {...(prefetchProps ? prefetchProps(c.id) : {})} data-testid={`client-row-${c.id}`} className={`border-b border-hairline last:border-b-0 hover:bg-surface2 ${c.archived ? "opacity-60" : ""}`}>
      <td className="px-4 py-3 min-w-0">
        <Link to={`/clients/${c.id}`} className="text-ink hover:text-gold-text truncate block">
          {c.name || <span className="text-faint italic">(Unnamed client{c.client_id ? ` — ${c.client_id}` : ""})</span>}
        </Link>
        {(c.location || syncTrouble || (c.is_duplicate_name && c.client_id)) && (
          <div className="mt-0.5 text-[10.5px] text-faint flex items-center gap-2" data-testid={`client-subline-${c.id}`}>
            {c.is_duplicate_name && c.client_id && <span data-testid={`client-dup-name-id-${c.id}`}>{c.client_id} · another client shares this name</span>}
            {c.location && <span>{c.location}</span>}
            {syncTrouble && (
              <span className="text-tone-red-text" data-testid={`client-sync-trouble-${c.id}`}>
                Sync {c.sync?.state === "conflict" ? "conflict" : "failed"}{c.sync?.last_synced_at ? ` ${new Date(c.sync.last_synced_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : ""}
              </span>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-ink-soft" data-testid={`client-open-projects-${c.id}`}>{c.open_projects || 0}</td>
      <td className="px-4 py-3 text-ink-soft" data-testid={`client-last-contact-${c.id}`}>{lastContactLabel(c.last_contact_days)}</td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          <Link to={`/clients/${c.id}`} data-testid={`client-open-btn-${c.id}`}
            className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1.5 text-[11px] text-ink-soft hover:border-brand hover:text-gold-text shrink-0">
            Open <ArrowRight className="h-3 w-3" />
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="More actions" data-testid={`client-overflow-btn-${c.id}`} className="inline-flex items-center justify-center rounded-md border border-hairline bg-paper h-8 w-8 hover:border-brand shrink-0">
                <MoreHorizontal className="h-4 w-4 text-ink-soft" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 p-1.5">
              {hasScope("client_portal.admin") && (
                <>
                  <Link to={`/clients/${c.id}`} data-testid={`client-manage-portal-${c.id}`}
                    className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2">
                    <ShieldCheck className="h-3.5 w-3.5" /> Manage Client Portal
                  </Link>
                  <button type="button" onClick={previewAsClient} disabled={!c.portal_enabled || previewing} data-testid={`client-preview-portal-${c.id}`}
                    className="flex flex-col items-start gap-0.5 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2 disabled:opacity-40 disabled:hover:bg-transparent w-full">
                    <span className="flex items-center gap-2.5"><Eye className="h-3.5 w-3.5" /> {previewing ? "Opening…" : "Preview Client Portal"}</span>
                    {!c.portal_enabled && <span className="text-[10.5px] text-faint pl-6">Enable Client Portal access first</span>}
                  </button>
                  <div className="h-px bg-hairline my-1.5 mx-1" />
                </>
              )}
              <ArchiveControls
                entity="clients" variant="menu"
                record={{ id: c.id, name: c.name, archived: c.archived }}
                canWrite={hasScope("clients.create")} isFounder={false}
                onChanged={onChanged}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

export function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasScope } = useAuth();
  const invalidatePortal = usePortalInvalidate();
  const [zoho, setZoho] = useState(null);
  const [zohoError, setZohoError] = useState(false);
  const [zohoLoading, setZohoLoading] = useState(false);
  const [zohoFetchedAt, setZohoFetchedAt] = useState(null);
  const [portalVisibility, setPortalVisibility] = useState({});
  const [statusExcluded, setStatusExcluded] = useState({});
  const [exclusionReasons, setExclusionReasons] = useState({});
  const [portalVisibilityLoaded, setPortalVisibilityLoaded] = useState(false);
  const [enablingPortal, setEnablingPortal] = useState(false);
  const [newType, setNewType] = useState(null); // "project" | "contact" | null
  const [editing, setEditing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const { data, isLoading: clientLoading, isError, error: queryError, refetch } = useClientDetailQuery(id);
  const load = () => { invalidatePortal.client(id); };
  const handleRecordCreated = () => {
    load();
    if (newType === "project") invalidatePortal.projectLists();
    if (newType === "contact") invalidatePortal.contacts();
  };
  const canSeeCommercialRole = ["founder", "partner", "finance"].includes(user?.role);
  const loadZoho = () => {
    if (!canSeeCommercialRole) return;
    setZohoLoading(true); setZohoError(false);
    api.get(`/v1/accounting/clients/${id}`)
      .then((r) => { setZoho(r.data); setZohoFetchedAt(new Date()); })
      .catch(() => setZohoError(true))
      .finally(() => setZohoLoading(false));
  };
  useEffect(() => { loadZoho(); }, [id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  // Perf fix (2026-09, NJM OS Phase-0 audit finding): ONE batched call for the whole "Manage
  // Client Portal" table instead of one GET per project row.
  useEffect(() => {
    if (!hasScope("client_portal.admin") || !data?.projects?.length) return;
    const ids = data.projects.map((p) => p.id).join(",");
    api.get(`/v1/client-portal/admin/projects/portal-visibility?ids=${ids}`)
      .then((r) => { setPortalVisibility(r.data.visibility || {}); setStatusExcluded(r.data.status_excluded || {}); setExclusionReasons(r.data.exclusion_reasons || {}); })
      .catch(() => { setPortalVisibility({}); setStatusExcluded({}); setExclusionReasons({}); })
      .finally(() => setPortalVisibilityLoaded(true));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data?.projects?.length, id]);
  // 2026-06 (Tessolve audit): the pilot flag is per-COMPANY and deny-by-default, so a client
  // that was never flagged has a "Manage Client Portal" table where every row looks fine and
  // nothing is actually reachable — and "Preview Client Portal" can't open at all. Staff-
  // clicked, explicit; nothing here auto-enables anything.
  const enablePortalAccess = async () => {
    const remoteId = data?.client?.sync?.remote_id;
    if (!remoteId) return;
    setEnablingPortal(true);
    try {
      await api.post(`/v1/client-portal/admin/portal-access/${remoteId}`, { enabled: true });
      toast.success("Client Portal access enabled for this client");
      load();
    } catch {
      toast.error("Couldn't enable Client Portal access");
    } finally {
      setEnablingPortal(false);
    }
  };
  if (clientLoading) return <DetailSkeleton testId="client-detail-skeleton" />;
  if (!data) {
    const status = queryError?.response?.status;
    return (
      <div className="p-8 text-sm text-faint" data-testid="client-detail-error">
        {status === 403 ? "Access Restricted" : status === 404 ? "Client not found" : (
          <>Couldn't load this client. <button type="button" onClick={() => refetch()} data-testid="client-detail-error-retry" className="font-semibold underline text-ink-soft">Retry</button></>
        )}
      </div>
    );
  }
  const c = data.client;
  const canSeeCommercial = canSeeCommercialRole;
  // Phase 5 fix (2026-10, Job C2) — the "⋯" trigger itself must not render when every item
  // inside it would be hidden for this user; mirrors the exact same conditions already used
  // below to decide each item (no new permission introduced, just read ahead of render).
  const canArchiveControlsClient = hasScope("clients.create") || user?.role === "founder";
  const hasClientOverflow = hasScope("contacts.create") || hasScope("client_portal.admin") || canArchiveControlsClient;
  const openCount = data.projects.filter((p) => !CLOSED_STATUSES.has(p.status)).length;
  const closedCount = data.projects.length - openCount;
  const projectsByStatus = data.projects.reduce((acc, p) => {
    const k = p.status || "Unassigned";
    (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});
  const visibleStatusEntries = Object.entries(projectsByStatus).filter(([status]) => showClosed || !CLOSED_STATUSES.has(status));
  const healthKey = toHealthKey(c.health);
  const healthNode = c.health
    ? (HEALTH_KEYS.includes(healthKey) ? <HealthMarker health={healthKey} testId="client-health-chip" /> : <RecordTag testId="client-health-chip">{c.health}</RecordTag>)
    : null;

  const askAi = () => {
    window.__njmSetAiContext?.({ client_id: c.id, label: c.name, skill: "client_brief" });
  };

  return (
    <RecordPageLayout
      testId="client-detail-page"
      titleTestId="client-detail-name"
      archivedTestId="client-archived-badge"
      activityTestId="client-activity-badge"
      title={c.name}
      statusChip={<StatusChip status={c.status} testId="client-status-chip" />}
      tags={[c.industry, c.type]}
      health={healthNode}
      activityIso={c.last_activity_at}
      archived={c.archived}
      brief={<span className="inline-flex items-center gap-1.5"><SyncPill state={c.sync?.state} lastSyncedAt={c.sync?.last_synced_at} /></span>}
      secondaryActions={<>
        {hasScope("clients.create") && (
          <button type="button" onClick={() => setEditing(true)} data-testid="client-edit-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-gold-text">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
        <Link to={`/commercial?client_id=${c.id}`} data-testid="draft-email-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-gold-text">
          <Mail className="h-3.5 w-3.5" /> Draft Email
        </Link>
        {hasScope("projects.create") && (
          <button type="button" onClick={() => setNewType("project")} data-testid="client-new-project-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-gold-text">
            <Plus className="h-3.5 w-3.5" /> New Project
          </button>
        )}
        <button type="button" data-testid="ask-ai-brief" onClick={askAi}
          className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-tone-gold-bg px-3 py-1.5 text-xs text-gold-text hover:bg-brand/20">
          <Sparkles className="h-3.5 w-3.5" /> Ask AI for brief
        </button>
      </>}
      primaryAction={
        <Link to={`/commercial?client_id=${c.id}`} data-testid="build-quote-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-paper hover:bg-ink/90">
          <Receipt className="h-3.5 w-3.5" /> Build Quote
        </Link>
      }
      overflow={hasClientOverflow && (<>
        {hasScope("contacts.create") && (
          <button type="button" onClick={() => setNewType("contact")} data-testid="client-new-contact-btn"
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2">
            <Plus className="h-3.5 w-3.5" /> New Contact
          </button>
        )}
        {hasScope("client_portal.admin") && (
          <button type="button"
            onClick={async () => { setPreviewing(true); await openClientPortalPreview(c.sync?.remote_id, c.name); setPreviewing(false); }}
            disabled={!c.portal_enabled || previewing}
            data-testid="client-detail-preview-portal-btn"
            title={!c.portal_enabled ? "Enable Client Portal access first" : ""}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2 disabled:opacity-40"
          >
            <Eye className="h-3.5 w-3.5" /> {previewing ? "Opening…" : "Preview Client Portal"}
          </button>
        )}
        {canArchiveControlsClient && <div className="h-px bg-hairline my-1.5 mx-1" />}
        <ArchiveControls
          entity="clients" variant="menu"
          record={{ id: c.id, name: c.name, archived: c.archived }}
          canWrite={hasScope("clients.create")}
          isFounder={user?.role === "founder"}
          onChanged={load}
          onPurged={() => { invalidatePortal.clientLists(); invalidatePortal.removeClientDetails([c.id]); navigate("/clients"); }}
        />
      </>)}
    >
      {isError && (
        <div className="-mt-4 flex items-center justify-between rounded-md border border-tone-gold-text/40 bg-tone-gold-bg px-3.5 py-2 text-xs text-gold-text" data-testid="client-detail-refresh-error">
          <span>Showing a saved copy — unable to refresh just now.</span>
          <button type="button" onClick={() => refetch()} data-testid="client-detail-refresh-retry" className="font-semibold underline">Retry</button>
        </div>
      )}

      <RecordFormModal
        type={newType || "project"}
        open={!!newType}
        onClose={() => setNewType(null)}
        defaultClientId={c.id}
        defaultClientName={c.name}
        onCreated={handleRecordCreated}
      />

      <RecordFormModal
        type="client"
        mode="edit"
        open={editing}
        onClose={() => setEditing(false)}
        recordId={c.id}
        initialValues={{ name: c.name, status: c.status, industry: c.industry, type: c.type, geography: c.geography || [], billing_currency: c.billing_currency }}
        onSaved={load}
      />

      {hasScope("client_portal.admin") && (
        <SectionPanel>
          <p className="text-[11px] uppercase tracking-wider text-[#78716C] mb-2">Client Settings</p>
          <EstimatorEnabledToggle entity="clients" recordId={c.id} initialEnabled={c.estimator_enabled} />
        </SectionPanel>
      )}

      {/* Commercial snapshot + live Zoho Books — gated to founder/partner/finance (backend
          already strips these fields for other roles); same gating key used by both cards. */}
      {canSeeCommercial && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-lg border border-hairline bg-paper p-4" data-testid="client-commercial-snapshot">
            <GatedCardHeader label="Commercial Snapshot" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Stat label="Revenue (Invoiced)" value={formatMoney(c.revenue, c.billing_currency)} />
              <Stat label="Lifetime Value" value={formatMoney(c.lifetime_value, c.billing_currency)} />
              <Stat label="Payment Behaviour" value={c.payment_behaviour || "—"} />
            </div>
          </div>

          <div className="rounded-lg border border-hairline bg-paper p-4" data-testid="client-zoho-snapshot">
            <div className="flex items-center justify-between mb-3">
              <GatedCardHeader label="Live Zoho Books" />
              <ZohoSyncState loading={zohoLoading} error={zohoError} fetchedAt={zohoFetchedAt} onRefresh={loadZoho} />
            </div>
            {zohoError ? (
              <div className="text-xs text-tone-red-text" data-testid="client-zoho-failed">Couldn't reach Zoho Books just now.</div>
            ) : !zoho ? (
              <div className="text-xs text-faint" data-testid="client-zoho-loading">Loading…</div>
            ) : !zoho.matched ? (
              <div className="text-xs text-faint" data-testid="client-zoho-unmatched">
                No Zoho Books match ({zoho.match_type || "unmatched"}) — set one from Receivables → Client ↔ Zoho Matching.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Stat label="Outstanding Balance" value={formatMoney(zoho.outstanding_balance, zoho.currency)} testId="zoho-outstanding-stat" />
                  <Stat label="Lifetime Invoiced" value={formatMoney(zoho.lifetime_invoiced, zoho.currency)} testId="zoho-lifetime-stat" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-faint">Overdue</div>
                    <div className={`text-xl mt-1 ${zoho.has_overdue ? "text-tone-gold-text" : "text-ink"}`} data-testid="zoho-overdue-flag">
                      {zoho.has_overdue ? `${zoho.overdue_count} overdue` : "None"}
                    </div>
                  </div>
                </div>
                {zoho.recent_invoices?.length > 0 && (
                  <div className="mt-4 divide-y divide-hairline">
                    {zoho.recent_invoices.slice(0, 5).map((inv) => (
                      <div key={inv.invoice_id} className="flex items-center justify-between py-1.5 text-xs">
                        <span className="text-ink-soft font-mono">{inv.invoice_number} · {inv.date}</span>
                        <span className="inline-flex items-center gap-2 text-ink">{formatMoney(inv.total, inv.currency_code)} <StatusChip status={inv.status} /></span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Stat label="Billing Currency" value={c.billing_currency || "—"} />
        <Stat label="Geography" value={(c.geography || []).join(", ") || "—"} />
        <Stat label="Projects" value={`${openCount} open · ${closedCount} closed`} testId="client-project-summary" />
      </div>

      <Section title="Projects" testId="client-projects-section">
        {data.projects.length === 0 ? (
          <EmptyState title="No projects yet" hint="Start a project for this client." testId="client-projects-empty"
            actionLabel={hasScope("projects.create") ? "+ New Project" : undefined} onAction={hasScope("projects.create") ? () => setNewType("project") : undefined} />
        ) : (
          <div className="rounded-md border border-hairline bg-paper divide-y divide-hairline">
            {visibleStatusEntries.map(([status, projects]) => (
              <div key={status}>
                <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-faint flex justify-between">
                  <StatusChip status={status} />
                  <span>{projects.length}</span>
                </div>
                {projects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-4 py-3 border-t border-hairline">
                    <Link to={`/projects/${p.id}`} data-testid={`client-project-row-${p.id}`} className="text-sm text-ink hover:text-gold-text">{p.name}</Link>
                    <div className="flex items-center gap-3">
                      <StageGlyph stage={p.status} testId={`client-project-stage-${p.id}`} />
                      {p.category && <span className="text-[10px] uppercase tracking-wider text-faint">{p.category}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {!showClosed && closedCount > 0 && (
              <button type="button" onClick={() => setShowClosed(true)} data-testid="client-projects-show-closed"
                className="w-full px-4 py-3 text-xs font-medium text-ink-soft hover:text-gold-text text-left">
                Show {closedCount} closed
              </button>
            )}
            {showClosed && closedCount > 0 && (
              <button type="button" onClick={() => setShowClosed(false)} data-testid="client-projects-hide-closed"
                className="w-full px-4 py-3 text-xs font-medium text-ink-soft hover:text-gold-text text-left">
                Hide closed
              </button>
            )}
          </div>
        )}
      </Section>

      {hasScope("client_portal.admin") && data.projects.length > 0 && (
        <Section title="Manage Client Portal" testId="client-manage-portal-section">
          <p className="text-xs text-faint px-4 pt-2 pb-1">Hide a project to remove it from every Client Portal surface (home, list, direct link, deliverables, documents, BTS, updates, messages, meetings) for this client — un-hide to restore it instantly.</p>
          {!c.portal_enabled && (
            <div className="mx-4 mt-1 mb-2 rounded-md border border-hairline bg-surface2 px-3 py-2.5" data-testid="client-portal-not-enabled-notice">
              <p className="text-xs text-ink-soft">Client Portal access is <span className="font-semibold">off</span> for {c.name} — nothing below is visible to them yet, and "Preview Client Portal" can't open until it's on.</p>
              {c.sync?.remote_id && (
                <button type="button" onClick={enablePortalAccess} disabled={enablingPortal} data-testid="client-portal-enable-access-btn"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-ink px-2.5 py-1.5 text-[11px] font-semibold text-paper hover:bg-ink/90 disabled:opacity-40">
                  {enablingPortal ? "Enabling…" : "Enable portal access"}
                </button>
              )}
            </div>
          )}
          {!portalVisibilityLoaded ? (
            <div className="px-4 py-3 text-xs text-faint" data-testid="client-portal-visibility-loading">Loading…</div>
          ) : (
            <div className="divide-y divide-hairline" data-testid="client-portal-visibility-table">
              {data.projects.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-4 py-3">
                  <Link to={`/projects/${p.id}`} className="text-sm text-ink hover:text-gold-text">{p.name}</Link>
                  <ClientPortalVisibilityToggle projectId={p.id} initialHidden={portalVisibility[p.id]} statusExcluded={statusExcluded[p.id]} status={p.status} exclusionReason={exclusionReasons[p.id]} />
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      <Section title="Contacts" testId="client-contacts-section"
        action={hasScope("contacts.create") && (
          <button type="button" onClick={() => setNewType("contact")} data-testid="client-contacts-new-contact-btn" className="text-xs font-medium text-gold-text hover:text-ink">+ New Contact</button>
        )}
      >
        {data.contacts.length === 0 ? (
          <EmptyState title="No contacts yet" hint="Add a contact for this client." testId="client-contacts-empty"
            actionLabel={hasScope("contacts.create") ? "+ New Contact" : undefined} onAction={hasScope("contacts.create") ? () => setNewType("contact") : undefined} />
        ) : (
          <div className="divide-y divide-hairline">
            {data.contacts.map((k) => (
              <div key={k.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <Link to={`/contacts/${k.id}`} data-testid={`client-contact-row-${k.id}`} className="text-sm text-ink hover:text-gold-text">{k.name}</Link>
                  <div className="text-[10px] text-faint">{k.role || k.role_title}</div>
                </div>
                <a href={`mailto:${k.email}`} className="text-xs text-ink-soft hover:text-gold-text inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />{k.email}</a>
              </div>
            ))}
          </div>
        )}
      </Section>

      {hasScope("client_portal.admin") && <PortalUsersSection client={c} contacts={data.contacts} />}
    </RecordPageLayout>
  );
}

/** Small "lock" eyebrow shown on every founder/partner/finance-only card — same gating as
 * the card itself, text only changes with the label. */
const GatedCardHeader = ({ label }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider text-gold-text mb-3 inline-flex items-center gap-1.5">
    <Wallet className="h-3 w-3" /> {label}
    <span className="ml-1.5 font-normal text-faint normal-case tracking-normal">· Founder · partner · finance only</span>
  </div>
);

/** Design System 2.0 (2026-10, Phase 5) — Zoho's own sync state, separate from the Notion
 * SyncPill: "Synced Xh ago" / a manual Refresh / "failed" + Retry. Same GET each time (no new
 * endpoint) — this only tracks fetch success/failure + fetch time, which the old silent
 * `.catch(() => setZoho(null))` never distinguished from "no Zoho match". */
function ZohoSyncState({ loading, error, fetchedAt, onRefresh }) {
  const rel = (() => {
    if (!fetchedAt) return null;
    const mins = Math.round((Date.now() - fetchedAt.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    return hrs < 24 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
  })();
  return (
    <div className="flex items-center gap-2 text-[11px]">
      {error ? (
        <>
          <span className="text-tone-red-text font-medium" data-testid="client-zoho-sync-failed">Sync failed</span>
          <button type="button" onClick={onRefresh} data-testid="client-zoho-retry-btn" className="text-gold-text hover:text-ink font-medium underline">Retry</button>
        </>
      ) : (
        <>
          {rel && <span className="text-faint" data-testid="client-zoho-synced-label">Synced {rel}</span>}
          <button type="button" onClick={onRefresh} disabled={loading} data-testid="client-zoho-refresh-btn"
            className="inline-flex items-center gap-1 text-ink-soft hover:text-gold-text disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </>
      )}
    </div>
  );
}

// Multiple Client Contacts (2026-09) — staff-facing Grant/Resend/Revoke for individual
// Contacts of THIS client company. See modules/client_portal.py's `/admin/companies/
// {company_remote_id}/portal-users*` routes for the access-derivation logic.
// Email failure UX + manual link sharing (2026-09-28 follow-up): `outcome !== "sent"` in
// Preview always (EMAIL_SENDS_DISABLED=true) — the "Copy sign-in link" action is a PERMANENT
// staff fallback, never conditional on the send having failed.
const EMAIL_OUTCOME_REASON = {
  skipped_not_configured: "emails are disabled in this environment",
  blocked_non_production: "blocked by the non-production email safeguard",
  failed: "delivery failed",
};
// Portal-user access never had a StatusChip-table meaning before (it was a bespoke
// emerald/amber/grey badge) — reuse the SAME chip shape/sizing via the shared tone classes,
// but keep its PRE-EXISTING colour meaning exactly (active=green, invited=gold, revoked=grey)
// rather than StatusChip's own table (which maps "invited"->blue, "revoked"->red — a
// different, unrelated meaning for those same words).
const PORTAL_ACCESS_TONE = { active: "green", invited: "gold", revoked: "grey" };

function emailNotSentToast(emailResult) {
  if (!emailResult || emailResult.outcome === "sent") return;
  const reason = EMAIL_OUTCOME_REASON[emailResult.outcome] || emailResult.error || "not sent";
  toast.warning(`Email not sent: ${reason}. Use "Copy sign-in link" to share it manually.`);
}

async function copyToClipboard(link) {
  if (!link) { toast.error("No link available yet — try again."); return false; }
  await navigator.clipboard.writeText(link);
  return true;
}

function PortalUsersSection({ client: c, contacts }) {
  const remoteId = c.sync?.remote_id;
  const [items, setItems] = useState(null);
  const [showGrant, setShowGrant] = useState(false);
  const [grantContactId, setGrantContactId] = useState("");
  const [granting, setGranting] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const guard = useEmailGuard();

  const load = () => {
    if (!remoteId) { setItems([]); return; }
    api.get(`/v1/client-portal/admin/companies/${remoteId}/portal-users`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [remoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Email Send Guard (2026-02): grant/resend below always update the Mongo record right
  // away — the magic-link EMAIL (always a client contact's own address, outside @njmpro.com)
  // waits for a human to confirm via the shared "This will email N people" modal.
  const grant = async () => {
    if (!grantContactId) { toast.error("Pick a contact"); return; }
    setGranting(true);
    try {
      const r = await api.post(`/v1/client-portal/admin/companies/${remoteId}/portal-users/grant`, { contact_id: grantContactId });
      const contactRemoteId = r.data?.contact_remote_id;
      if (guard.intercept(r.data, async (typedCount) => {
        const cr = await api.post(`/v1/client-portal/admin/companies/${remoteId}/portal-users/${contactRemoteId}/confirm-send`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        toast.success("Access granted");
        emailNotSentToast(cr.data?.email_result);
        load();
      })) { setShowGrant(false); setGrantContactId(""); return; }
      toast.success("Access granted");
      emailNotSentToast(r.data?.email_result);
      setShowGrant(false); setGrantContactId("");
      load();
    } catch (e) {
      toast.error((e?.response?.data?.detail?.code || "").replaceAll("_", " ") || "Couldn't grant access");
    } finally { setGranting(false); }
  };

  const resend = async (contactRemoteId) => {
    setBusyId(contactRemoteId);
    try {
      const r = await api.post(`/v1/client-portal/admin/companies/${remoteId}/portal-users/${contactRemoteId}/resend`);
      if (guard.intercept(r.data, async (typedCount) => {
        const cr = await api.post(`/v1/client-portal/admin/companies/${remoteId}/portal-users/${contactRemoteId}/confirm-send`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        toast.success("Invite resent");
        emailNotSentToast(cr.data?.email_result);
      })) return;
      toast.success("Invite resent");
      emailNotSentToast(r.data?.email_result);
    } catch (e) {
      toast.error((e?.response?.data?.detail?.code || "").replaceAll("_", " ") || "Couldn't resend invite");
    } finally { setBusyId(null); }
  };

  const copyLink = async (contactRemoteId) => {
    setBusyId(contactRemoteId);
    try {
      const r = await api.post(`/v1/client-portal/admin/companies/${remoteId}/portal-users/${contactRemoteId}/resend`);
      // `link` is always minted whether or not the send itself is still pending confirmation —
      // copying it never needs to wait on the modal.
      const ok = await copyToClipboard(r.data?.email_result?.link);
      if (ok) toast.success("Sign-in link copied to clipboard");
      else if (r.data?.confirmation_required) toast.error("No link available yet — confirm the send first, then try Copy again");
    } catch (e) {
      toast.error((e?.response?.data?.detail?.code || "").replaceAll("_", " ") || "Couldn't get a link");
    } finally { setBusyId(null); }
  };

  const revoke = async (contactRemoteId) => {
    setBusyId(contactRemoteId);
    try {
      await api.post(`/v1/client-portal/admin/companies/${remoteId}/portal-users/${contactRemoteId}/revoke`);
      toast.success("Access revoked");
      load();
    } catch {
      toast.error("Couldn't revoke access");
    } finally { setBusyId(null); }
  };

  return (
    <Section title="Portal Users" testId="client-portal-users-section">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-hairline">
        <p className="text-xs text-faint">
          Contacts who can sign in to this client's portal.
          {!c.portal_enabled && " Client Portal access is currently OFF for this company — you can still grant here, they'll be able to sign in once it's turned on."}
        </p>
        <button type="button" data-testid="portal-users-grant-btn" onClick={() => setShowGrant(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-gold-text whitespace-nowrap">
          <Plus className="h-3.5 w-3.5" /> Grant access
        </button>
      </div>
      {items === null ? (
        <div className="px-4 py-6 text-xs text-faint" data-testid="portal-users-loading">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No portal users yet" hint="Grant access to a contact so they can sign in." testId="portal-users-empty" />
      ) : (
        <div className="divide-y divide-hairline" data-testid="portal-users-list">
          {items.map((u) => {
            const tone = PORTAL_ACCESS_TONE[u.access] || "grey";
            return (
              <div key={u.contact_remote_id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`portal-user-row-${u.contact_remote_id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{u.name}</div>
                  <div className="text-[10.5px] text-faint truncate">{u.email || "—"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span data-testid={`portal-user-access-${u.contact_remote_id}`} className={`inline-flex items-center gap-1.5 h-[22px] rounded px-2 text-[11px] font-medium leading-none ${TONE_CHIP_CLS[tone]}`}>
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${TONE_DOT_CLS[tone]}`} /> {u.access}
                  </span>
                  {u.access !== "revoked" && (
                    <button type="button" data-testid={`portal-user-resend-${u.contact_remote_id}`} disabled={busyId === u.contact_remote_id} onClick={() => resend(u.contact_remote_id)}
                      className="rounded-md border border-hairline px-2.5 py-1 text-[11px] text-ink-soft hover:border-brand disabled:opacity-40">Resend invite</button>
                  )}
                  {u.access !== "revoked" && (
                    <button type="button" data-testid={`portal-user-copy-link-${u.contact_remote_id}`} disabled={busyId === u.contact_remote_id} onClick={() => copyLink(u.contact_remote_id)}
                      title="Mint a fresh single-use sign-in link and copy it, so you can share it directly if the email never arrives"
                      className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1 text-[11px] text-ink-soft hover:border-brand disabled:opacity-40">
                      <Copy className="h-3 w-3" /> Copy sign-in link
                    </button>
                  )}
                  <button type="button" data-testid={`portal-user-revoke-${u.contact_remote_id}`} disabled={busyId === u.contact_remote_id || u.access === "revoked"} onClick={() => revoke(u.contact_remote_id)}
                    className="rounded-md border border-tone-red-text/30 px-2.5 py-1 text-[11px] text-tone-red-text hover:border-tone-red-text/60 disabled:opacity-40">Revoke</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showGrant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" data-testid="portal-users-grant-modal">
          <div className="w-full max-w-sm rounded-lg bg-paper p-5 space-y-3">
            <h3 className="font-editorial text-lg text-ink">Grant portal access</h3>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-faint">Contact</label>
              <select data-testid="portal-users-grant-contact-select" value={grantContactId} onChange={(e) => setGrantContactId(e.target.value)}
                className="mt-1 w-full rounded-md border border-hairline px-3 py-2 text-sm bg-paper text-ink">
                <option value="">Select a contact…</option>
                {contacts.map((k) => <option key={k.id} value={k.id}>{k.name} ({k.email || "no email"})</option>)}
              </select>
            </div>
            <button data-testid="portal-users-grant-add-contact-link" type="button" onClick={() => { setShowGrant(false); setShowAddContact(true); }}
              className="text-xs text-gold-text hover:underline">+ Person not listed? Add a new contact</button>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="portal-users-grant-cancel" onClick={() => setShowGrant(false)} className="rounded-md border border-hairline px-3 py-1.5 text-xs text-ink-soft">Cancel</button>
              <button type="button" data-testid="portal-users-grant-confirm" disabled={granting} onClick={grant}
                className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50">{granting ? "Granting…" : "Grant access"}</button>
            </div>
          </div>
        </div>
      )}

      <RecordFormModal type="contact" open={showAddContact} onClose={() => setShowAddContact(false)}
        defaultClientId={c.id} defaultClientName={c.name}
        onCreated={(record) => { setShowAddContact(false); setGrantContactId(record.id); setShowGrant(true); }} />
      <EmailConfirmModal
        open={guard.open}
        preview={guard.preview}
        busy={guard.busy}
        onConfirm={guard.confirm}
        onCancel={guard.cancel}
      />
    </Section>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-hairline bg-paper p-4" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-faint">{label}</div>
    <div className="text-xl mt-1 text-ink">{value}</div>
  </div>
);
